import { last, sortBy } from 'lodash';
import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import enums from '../enum';
import { Map } from 'immutable';
import * as actions from '../actions';
import componentServices from 'Scripts/componentServices';
import { FieldAppContext, IFieldAppState } from 'Scripts/contexts/fieldAppProvider';
import siteVisitFactory, { ISiteVisit, SiteVisitStatus, SiteVisitType } from 'Scripts/models/siteVisitModel';
import { Expander, Radio } from './sharedComponents';
import { IJobCost } from 'Scripts/models/jobCostModel';
import inspectionChecklistItemFactory, { ChecklistItemStatus, IInspectionChecklistItem } from 'Scripts/models/inspectionChecklistItemModel';
import { getCollection, getModel, ModelCollection } from 'Scripts/store';
import { useSaveRoot } from 'Scripts/saveRoot';
import { IPatchOperation, PatchOpType } from 'Scripts/modelFactory';
import { inspectionModelFacadeFactory, IInspectionModelFacade } from 'Scripts/models/inspectionModelFacade';
import { IJobCostSpecification } from 'Scripts/models/jobCostSpecificationModel';
import { IJobCostWorkArea } from 'Scripts/models/jobCostWorkAreaModel';
import jobIssueFactory, { IJobIssue } from 'Scripts/models/jobIssueModel';
import JobIssueEditModal from './jobIssueEdit';
import { saveSiteVisit } from 'Scripts/siteVisitActions';
import serviceGateway from 'Scripts/serviceGateway';

function InspectionSubmitModal({inspection}: {inspection: IInspectionModelFacade}) {
	const { store } = componentServices;
	const [mutableInspection, setMutableInspection] = useState(() => inspectionModelFacadeFactory(inspection.toDocument()));
	const jobCost = getModel({ store, collection: ModelCollection.jobCosts, key: inspection.visit.jobCosts?.toArray()[0].jobCostKey});
	const updateInspectionQuality = useCallback((val: number) => {
		const updated = inspectionModelFacadeFactory(mutableInspection.toDocument());

		updated.visit.installationQuality = val;

		setMutableInspection(updated);
	}, [mutableInspection]);
	const updateHoldbackRelease = useCallback((val: number) => {
		const updated = inspectionModelFacadeFactory(mutableInspection.toDocument());

		updated.visit.requestHoldbackRelease = !!val;

		setMutableInspection(updated);
	}, [mutableInspection]);
	const qualityButtons = useMemo(() => {
		const buttons = [];

		for(let i = 1; i < 11; i ++){
			buttons.push({classNames:['icon'], key: i, label: i.toString(), isSelected: i === mutableInspection.visit.installationQuality })
		}

		return buttons;
	}, [mutableInspection]);
	const holdbackButtons = useMemo(() => {
		const hasHoldbackSelect = typeof mutableInspection.visit.requestHoldbackRelease !== 'undefined';
		const buttons = [
			{classNames:['icon'], key: 0, label: 'No', isSelected: hasHoldbackSelect && !mutableInspection.visit.requestHoldbackRelease },
			{classNames:['icon'], key: 1, label: 'Yes', isSelected: hasHoldbackSelect && mutableInspection.visit.requestHoldbackRelease }
		];

		return buttons;
	}, [mutableInspection]);
	const saveInspection = async () => {
		const jobPatch = mutableInspection.issues.map(i => ({ op: PatchOpType.add, path: `/jobIssues/-`, value: i.toDocument()}));
		const patch:IPatchOperation[] = [
			{ op: PatchOpType.replace, path: '/installationQuality', value: mutableInspection.visit.installationQuality },
			{ op: PatchOpType.replace, path: '/requestHoldbackRelease', value: mutableInspection.visit.requestHoldbackRelease },
			{ op: PatchOpType.replace, path: '/totalMinutes', value: 0 }, // TODO: get the total minutes from the time log
			{ op: PatchOpType.replace, path: '/statusLog/-', value: { status: SiteVisitStatus.completed } }, // move this to the controller?
			{ op: PatchOpType.add, path: '../job', value: jobPatch }
		];
		for(const item of mutableInspection.visit.inspectionChecklistItems) {
			patch.push({op: PatchOpType.add, path: '/inspectionChecklistItems/-', value: item.toDocument()});
		}
		// TODO: push this into a submission queue that manages retries, watches online/offline status, etc.
		try {
			await serviceGateway.submitFieldVisit({ key: mutableInspection.visit.key, patch });
			actions.popModal();
		}
		catch (err) {
			alert(`${err}`);
		}
	};

	

	return <div className='window'>
		<header>
			<button className='cancel' onClick={actions.popModal}><span>Cancel</span></button>
			<span>Finalize Inspection</span>
			<button className={mutableInspection.canFinalize? 'save': 'save disabled'} onClick={mutableInspection.canFinalize? saveInspection: undefined}><span>Finalize</span></button>
		</header>
		<div className='views'>
			<div className='content in scrollable'>
				<table className='editIssue' cellPadding='0' cellSpacing='0'>
					<tbody>
						<tr>
							<td></td>
							<td><strong>Inspecting</strong></td>
							<td>
								TODO: Populate this
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Installation Quality</strong></td>
							<td>
								<Radio buttons={qualityButtons} onChange={selected => updateInspectionQuality(selected.key) } reportAllEvents={true} />
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Release Final 10%</strong></td>
							<td>
								<Radio buttons={holdbackButtons} onChange={selected => updateHoldbackRelease(selected.key) } reportAllEvents={true} />
							</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Inspector Signature</strong></td>
							<td>TODO</td>
							<td></td>
						</tr>
						<tr>
							<td></td>
							<td><strong>Customer Signature</strong></td>
							<td>TODO</td>
							<td></td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	</div>
}

function ChecklistItem({item, onStatusChange}: {item: IInspectionChecklistItem, onStatusChange: (status: ChecklistItemStatus) => void}) {
	const rootClassNames = ['inspectionItem'];
	const isDisabled = !!item.relayId; // this is a previously persisted checklist item
	const buttons = [
		{classNames:['icon', 'yes'], key: ChecklistItemStatus.pass, isSelected: item.status === ChecklistItemStatus.pass },
		{classNames:['icon', 'no'], key: ChecklistItemStatus.fail, isSelected: item.status === ChecklistItemStatus.fail },
		{classNames:['icon', 'na'], key: ChecklistItemStatus.notApplicable, isSelected: item.status === ChecklistItemStatus.notApplicable }
	]

	if(isDisabled) {
		rootClassNames.push('disabled');
	}

	switch(item.status) {
	case ChecklistItemStatus.fail:
		rootClassNames.push('no');
		break;
	case ChecklistItemStatus.pass:
		rootClassNames.push('yes');
		break;
	case ChecklistItemStatus.notApplicable:
		rootClassNames.push('na');
		break;
	}

	return <div className={rootClassNames.join(' ')}>
		<Radio buttons={buttons} onChange={!isDisabled? selected => onStatusChange(selected.key): undefined } reportAllEvents={true} />

		<div className='description'>
			<div className='property'>{item.name}</div>
			<div className='value'>{item.value}</div>
		</div>
	</div>
}

function InspectionWorkArea({inspectionConfig, issues, visit, jobCost, updateChecklistItemStatus, saveIssue, workArea}: 
	{ 
		inspectionConfig: any, 
		issues: IJobIssue[],
		visit: ISiteVisit, 
		jobCost: IJobCost, 
		updateChecklistItemStatus: (i: IInspectionChecklistItem, s: ChecklistItemStatus) => void, 
		saveIssue(issue: IJobIssue): void;
		workArea: IJobCostWorkArea
	}) {
	const { store } = componentServices;
	const addDeficiency = useCallback(() => {
		const issue = jobIssueFactory({jobCostKey: jobCost.key, workAreaKey: workArea.key, changeOrderKey: visit.jobCosts.toArray()[0].changeOrderKey });
		actions.pushModal({
			component: <JobIssueEditModal saveIssue={saveIssue} inspectionConfig={inspectionConfig} issue={issue } />
		});
	}, [saveIssue, inspectionConfig])
	// populate our checklists
	const checklistItems = useMemo(() => {
		const items: IInspectionChecklistItem[] = [];
			
		if(visit) {
			//"valueExpressions": [{"group": "*", "pattern": "^$"}]
			const { checklist: { exclude, staticItems} } = inspectionConfig;
			const specs = sortBy(jobCost.specifications.filter(spec => spec.workAreaKey === workArea.key && CHECKLIST_SPEC_GROUPS.indexOf(spec.group) !== -1 && !testChecklistExcludeExpressions(spec, exclude)), 'sortIndex');
			
			const previousChecklistItems = getCollection({ store, collection: ModelCollection.siteVisits}).filter(v => v.key !== visit.key && v.type === SiteVisitType.inspection && v.jobCosts[0].jobCostKey === jobCost.key).toArray().flatMap(v => v.inspectionChecklistItems.toArray());
	
			for(const staticItem of staticItems) {
				let checklistItem = visit.inspectionChecklistItems.find(i => i.workAreaKey == workArea.key && i.name === staticItem.value && !i.value) || previousChecklistItems.find(i => i.workAreaKey == workArea.key && i.name === staticItem.value && !i.value);

				if(!checklistItem) {
					checklistItem = inspectionChecklistItemFactory({name: staticItem.value, workAreaKey: workArea.key, status: ChecklistItemStatus.unknown});
				}

				items.push(checklistItem);
			}

			for(const spec of specs) {
				let checklistItem = visit.inspectionChecklistItems.find(i => i.workAreaKey === workArea.key && i.name === spec.name && i.value === spec.value) || previousChecklistItems.find(i => i.workAreaKey == workArea.key && i.name === spec.name && i.value === spec.value);

				if(!checklistItem) {
					checklistItem = inspectionChecklistItemFactory({name: spec.name, value: spec.value, workAreaKey: workArea.key, status: ChecklistItemStatus.unknown});
				}

				items.push(checklistItem);
			}
		}
	
		return items;
	}, [visit, jobCost, workArea]);

	const workAreaIssues:IJobIssue[] = useMemo(() => {
		return issues?.filter(i => i.workAreaKey === workArea.key);
	}, [visit, jobCost, workArea]);

	return <Expander title={workArea.name} subTitle={`${checklistItems.filter(i => i.status !== ChecklistItemStatus.unknown).length} of ${checklistItems.length}`} contentClassNames={['inner']}>
		<div className='inspectionItems'>
			{ checklistItems.map(i => <ChecklistItem key={i.key} item={i} onStatusChange={status => updateChecklistItemStatus(i, status)} />) }
		</div>
		<table className='sortable compact interactive' cellPadding='0' cellSpacing='0'>
			<tbody>
				{workAreaIssues?.map(i => <tr>
					<td></td>
					<td className='source'>{i.source}</td>
					<td className='description'>{i.description}</td>
					<td className='estimate'>${i.estimatedMinutes} minutes</td>
					<td className='action'><button className='icon iconOnly photos disabled'><span>0</span></button></td>
					<td className='action'><button className='icon iconOnly contextMenu'><span></span></button></td>
					<td></td>
				</tr>)}
				
			</tbody>
		</table>
		<footer className='inspectionActions'>
			<button className="icon iconLabel deficiency" onClick={addDeficiency}><span>ADD DEFICIENCY</span></button>
			<button className="icon iconLabel note"><span>ADD NOTE</span></button>
			<button className="icon iconLabel "><span>MARK ALL N/A</span></button>
			<button className="icon iconLabel ok"><span>MARK ALL OK</span></button>
		</footer>
	</Expander>
}

interface IConfigExcludeExpression {
	group: string;
	pattern: string;
}

function testChecklistExcludeExpressions(spec: IJobCostSpecification, exclude: { keyExpressions?: IConfigExcludeExpression[], valueExpressions?: IConfigExcludeExpression[]}) {
	if(exclude.keyExpressions && exclude.keyExpressions.some(exp => {
		if(!exp.group || exp.group === '*' || Number(exp.group) === spec.group) {
			return new RegExp(exp.pattern).test(spec.key);
		}
	})) {
		return true;
	}
	if(exclude.valueExpressions && exclude.valueExpressions.some(exp => {
		if(!exp.group || exp.group === '*' || Number(exp.group) === spec.group) {
			return new RegExp(exp.pattern).test(spec.value);
		}
	})) {
		return true;
	}
}

const CHECKLIST_SPEC_GROUPS = [enums.specGroups.Cabinet, enums.specGroups.Work_Area, enums.specGroups.Work_Area_Custom];
export function FieldInspectionVisit() {
	const { router, routeParams, store } = componentServices;
	const [componentTimeLog, setComponentTimeLog] = useState([]);
	const { fieldActions, fieldState } = useContext(FieldAppContext);
	const { jobcostkey: jobCostKey, visitkey: visitKey } = routeParams;
	const jobCost = getModel({ store, collection: ModelCollection.jobCosts, key: jobCostKey });
	const { inspectionConfig } = actions.getInspectionConfig(jobCost);
	const [saveRoot] = useSaveRoot<IInspectionModelFacade>({
		autoSave:  true,
		globalSave: false,
		warnOnLeave: true,
		stateId: `FieldInspectionVisit-${visitKey}`,
		modelFactory: inspectionModelFacadeFactory,
		resolveMutableModel (current) {
			let mutable = current;
			const active = fieldState.activeInspections[visitKey];
			let rebase = false;

			if(!mutable || (active && active.version !== mutable.version)) {
				if(active) {
					mutable = inspectionModelFacadeFactory(active.toDocument() as IInspectionModelFacade);
					rebase = true;
				}
				else {
					const visit = getModel({ collection: ModelCollection.siteVisits, key: visitKey });

					mutable = inspectionModelFacadeFactory({ visit, issues: [], timeLog: []});
				}
			}

			return [ mutable, rebase ]
		},
		save: (doc, patch: IPatchOperation[]) => {
			return fieldActions.setInspectionData(doc);
		},
		savedCallback: (status) => {
			// if (!status.error) {
			// 	props.close();
			// }
		}
	});

	const updateChecklistItemStatus = useCallback((item: IInspectionChecklistItem, status: ChecklistItemStatus) => {
		saveRoot.update(model => {
			const updated = inspectionChecklistItemFactory(item.toDocument());

			updated.status = updated.status === status? ChecklistItemStatus.unknown: status;

			model.setChecklistItem(updated);
		}, {autoSave: true});
	}, [saveRoot.update]);

	const saveIssue = useCallback((issue:IJobIssue) => {
		saveRoot.update(model => {
			const existingIndex = model.issues.findIndex(i => i.key === issue.key);

			if(existingIndex === -1) {
				model.addIssue(issue);
			}
			else {
				model.updateIssue(issue);
			}

			actions.popModal();
		}, {autoSave: true});
	},[saveRoot.model]);

	const workAreas = useMemo(() => {
		return sortBy(jobCost.workAreas, 'sortIndex');
	}, [jobCost]);



	// TODO: work out time logging
	// useEffect(() => {
	// 	const timeLog = componentTimeLog.concat();

	// 	timeLog.push({startMs: Date.now()});
	// 	setComponentTimeLog(timeLog);

	// 	return () => {
	// 		const lastLog = last(componentTimeLog)
	// 		if(lastLog && !lastLog.endMs) {
	// 			lastLog.endMs = Date.now();
	// 		}
	// 		saveRoot.update(model => {
	// 			for(const item of timeLog) {
	// 				model.logTime(item);
	// 			}
	// 		}, {})
	// 	}
	// }, []);

	useEffect(() => {
		const key = 'inspection-header-button-complete';
		actions.setHeaderAction({key, element: <button onClick={() => actions.pushModal({component: <InspectionSubmitModal inspection={saveRoot.model} />})}>Complete</button> })

		return () => {
			actions.deleteHeaderAction({key})
		}
	}, [saveRoot.model]);


	return <div className='dashboard fieldApp'>
		<section className='expander expanded'>
			<div className='inner centered'>
				<h1>TODO: Inspecting <span>2. Install, 3. Post Install Glass Inserts</span></h1>
			</div>
		</section>
		{ workAreas.map(workArea => <InspectionWorkArea key={workArea.key} jobCost={jobCost} workArea={workArea} inspectionConfig={inspectionConfig} visit={saveRoot.model?.visit} updateChecklistItemStatus={updateChecklistItemStatus} saveIssue={saveIssue} issues={saveRoot.model?.issues} />)}
	</div>;
}